import React, { useEffect, useState } from 'react';
import { Container, makeStyles } from '@material-ui/core';
import Page from 'src/components/Page';
import useAuth from 'src/hooks/useAuth';
import { useMaintenancePackage } from 'src/hooks/useMaintenancePackage';
import { getDefaultMaintenancePackagesFilters } from 'src/components/MaintenancePackages/Filters/utils';
import {
    ADMIN_LEVEL_ACCESS,
    DEVELOPER_LEVEL_ACCESS,
    GROUP_LEVEL_ACCESS,
    TAccessLevel,
    USER_LEVEL_ACCESS,
} from 'src/components/access';
import { useTranslation } from 'react-i18next';
import { IStore } from 'src/interfaces/store';
import { TFiltersOptions } from './types';
import { Results } from './Results';
import { LOCAL_STORAGE_MAINTENANCE_PACKAGE_FILTERS } from './constants';
import { IFiltersMaintenancePackages } from 'src/components/MaintenancePackages/Filters/components/types';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100%',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
    },
}));

export const MaintenancePackageList = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { searchMaintenancePackagesByFilters } = useMaintenancePackage();
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(
        getDefaultMaintenancePackagesFilters()
    );
    const [isReady, setIsReady] = useState(false);

    const handleInitializeStartFilters = (accessLevel: TAccessLevel) => {
        if (!accessLevel) return false;

        let options: TFiltersOptions = {};

        if (accessLevel === DEVELOPER_LEVEL_ACCESS) return true;

        if (
            accessLevel === ADMIN_LEVEL_ACCESS ||
            accessLevel === USER_LEVEL_ACCESS
        ) {
            options['stores'] = user?.stores.map((store: IStore) => {
                return {
                    label: store.name,
                    value: store._id,
                };
            });
        }

        if (accessLevel === GROUP_LEVEL_ACCESS) {
            if (!user?.group) return false;

            options['stores'] = user?.group?.stores.map((store: IStore) => {
                return {
                    label: store.name,
                    value: store._id,
                };
            });
        }

        return options;
    };

    const handleInitializeFilters = () => {
        const saved = localStorage.getItem(
            LOCAL_STORAGE_MAINTENANCE_PACKAGE_FILTERS
        );

        if (saved) {
            setFilters(JSON.parse(saved));
        } else {
            if (!user.accessLevel) return false;

            const response = handleInitializeStartFilters(user?.accessLevel);

            if (!response) return;

            const defaultFilters = getDefaultMaintenancePackagesFilters();

            let responseObject: TFiltersOptions = {};

            if (typeof response !== 'boolean') responseObject = response;

            const finalQuery = {
                ...defaultFilters,
                ...responseObject,
                page: defaultFilters.page + 1,
            };

            searchMaintenancePackagesByFilters(finalQuery);
        }
        setIsReady(true);
    };

    const loadInformation = async () => {
        if (!isReady) return;

        const response = handleInitializeStartFilters(user?.accessLevel);

        if (!response) return;

        let finalQuery: IFiltersMaintenancePackages = {
            ...filters,
            page: filters.page + 1,
        };

        let responseObject: TFiltersOptions = {};

        if (typeof response !== 'boolean') responseObject = response;

        if (
            filters.stores?.length === 0 &&
            responseObject.stores &&
            responseObject.stores?.length > 0
        )
            finalQuery.stores = responseObject.stores;

        searchMaintenancePackagesByFilters(finalQuery, {
            isReadPermissionNecessary: true,
        });

        localStorage.setItem(
            LOCAL_STORAGE_MAINTENANCE_PACKAGE_FILTERS,
            JSON.stringify(filters)
        );
    };

    useEffect(() => {
        if (user?._id) handleInitializeFilters();
    }, [user]);

    useEffect(() => {
        loadInformation();
    }, [filters]);

    return (
        <Page className={classes.root} title={t('maintenancePackage.List')}>
            <Container maxWidth={false}>
                <Results
                    values={filters}
                    setValues={setFilters}
                    loadInformation={loadInformation}
                    query={query}
                    setQuery={setQuery}
                    currentTab={'all'}
                />
            </Container>
        </Page>
    );
};
